import React, { useState } from 'react';
import BaseModal from './BaseModal';
import Button from '../common/Button';

const MintLicensesModal = ({
  isOpen,
  onClose,
  mintAmount,
  setMintAmount,
  publicPrices,
  tierEnabled,
  onMint,
}) => {
  const [selectedTier, setSelectedTier] = useState(1);

  const handleSubmit = () => {
    if (!mintAmount || mintAmount <= 0) return;
    onMint(selectedTier, mintAmount);
  };

  const tierPrice = publicPrices[selectedTier] || 0;
  const totalPrice = (mintAmount * tierPrice).toFixed(4);

  const tiers = [
    { id: 1, name: 'Normal', description: 'Basic license for personal use' },
    { id: 2, name: 'Pro', description: 'Enhanced features and support' },
  ];

  // Filter tiers based on individual tier enabled status
  const availableTiers = (!tierEnabled || typeof tierEnabled !== 'object') 
    ? tiers 
    : tiers.filter(tier => {
        const isEnabled = tier.id === 1 ? tierEnabled[1] : tierEnabled[2];
        return isEnabled === true;
      });

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Mint Licenses"
      subtitle="Purchase new licenses directly"
      icon="🪙"
      footer={
        <div style={{
          display: 'flex',
          gap: window.innerWidth < 640 ? '12px' : '16px', // Responsive gap
          flexDirection: window.innerWidth < 640 ? 'column' : 'row' // Stack buttons on mobile
        }}>
          <Button
            variant="secondary"
            onClick={onClose}
            style={{
              flex: 1,
              width: window.innerWidth < 640 ? '100%' : 'auto', // Full width on mobile
              height: window.innerWidth < 640 ? '52px' : '48px', // Taller on mobile
              fontSize: window.innerWidth < 640 ? '16px' : '14px' // Larger font on mobile
            }}
          >
            Cancel
          </Button>
          <Button
            variant="purple"
            onClick={handleSubmit}
            disabled={!mintAmount || mintAmount <= 0 || availableTiers.length === 0}
            style={{
              flex: 1,
              width: window.innerWidth < 640 ? '100%' : 'auto', // Full width on mobile
              height: window.innerWidth < 640 ? '52px' : '48px', // Taller on mobile
              fontSize: window.innerWidth < 640 ? '16px' : '14px' // Larger font on mobile
            }}
          >
            <span style={{
              marginRight: '8px'
            }}>🪙</span> Mint
          </Button>
        </div>
      }
    >
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: window.innerWidth < 640 ? '16px' : '24px' // Responsive spacing
      }}>
        {/* Tier Selection */}
        {availableTiers.length === 0 ? (
          <div style={{
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            backdropFilter: 'blur(10px)',
            borderRadius: '16px',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            padding: window.innerWidth < 640 ? '16px' : '24px',
            boxShadow: '0 8px 32px rgba(239, 68, 68, 0.1)'
          }}>
            <div style={{
              textAlign: 'center',
              color: 'white'
            }}>
              <p style={{
                fontSize: '18px',
                fontWeight: '600',
                margin: '0 0 8px 0',
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
              }}>🚫 No Tiers Available</p>
              <p style={{
                fontSize: '14px',
                color: 'rgba(255, 255, 255, 0.7)',
                margin: '0',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
              }}>No license tiers are currently enabled</p>
            </div>
          </div>
        ) : (
          <div style={{
            backgroundColor: 'rgba(55, 65, 81, 0.1)',
            backdropFilter: 'blur(10px)',
            borderRadius: '16px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            padding: window.innerWidth < 640 ? '16px' : '24px',
            boxShadow: '0 8px 32px rgba(55, 65, 81, 0.1)'
          }}>
            <label style={{
              display: 'block',
              fontWeight: '500',
              color: 'white',
              marginBottom: '12px',
              fontSize: '14px',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
            }}>
              Select License Tier
            </label>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '20px'
            }}>
              {availableTiers.map((tier) => (
                <button
                  key={tier.id}
                  onClick={() => setSelectedTier(tier.id)}
                  style={{
                    width: '100%',
                    padding: window.innerWidth < 640 ? '16px' : '24px',
                    border: `2px solid ${selectedTier === tier.id ? '#8b5cf6' : 'rgba(255, 255, 255, 0.2)'}`,
                    backgroundColor: selectedTier === tier.id ? 'rgba(139, 92, 246, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    borderRadius: '16px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    textAlign: 'left',
                    minHeight: '140px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start'
                  }}
                >
                  <div style={{
                    fontWeight: '600',
                    fontSize: '18px',
                    marginBottom: '10px',
                    lineHeight: '1.2',
                    wordBreak: 'break-word'
                  }}>{tier.name}</div>
                  <div style={{
                    fontSize: '14px',
                    color: 'rgba(255, 255, 255, 0.85)',
                    marginBottom: '14px',
                    lineHeight: '1.4',
                    wordBreak: 'break-word'
                  }}>{tier.description}</div>
                  <div style={{
                    fontSize: '20px',
                    fontWeight: '700',
                    color: '#c084fc',
                    marginTop: 'auto',
                    wordBreak: 'break-word'
                  }}>{publicPrices[tier.id] || '0.00'} ETCPOWv2</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {availableTiers.length > 0 && (
          <>
            {/* Price Display */}
            <div style={{
              backgroundColor: 'rgba(139, 92, 246, 0.1)',
              backdropFilter: 'blur(10px)',
              borderRadius: '16px',
              border: '1px solid rgba(139, 92, 246, 0.3)',
              padding: window.innerWidth < 640 ? '16px' : '24px',
              boxShadow: '0 8px 32px rgba(139, 92, 246, 0.1)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '24px'
              }}>
                <div>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: 'white',
                    margin: '0',
                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                  }}>{tiers.find(t => t.id === selectedTier)?.name}</h3>
                  <p style={{
                    fontSize: '14px',
                    color: 'rgba(255, 255, 255, 0.7)',
                    margin: '4px 0 0',
                    textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                  }}>{tiers.find(t => t.id === selectedTier)?.description}</p>
                </div>
                <div style={{
                  textAlign: 'right'
                }}>
                  <p style={{
                    fontSize: '14px',
                    color: 'rgba(255, 255, 255, 0.7)',
                    margin: '0',
                    textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                  }}>Price per license</p>
                  <p style={{
                    fontSize: '28px',
                    fontWeight: '700',
                    color: 'white',
                    margin: '4px 0 0',
                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                  }}>{tierPrice} ETCPOWv2</p>
                </div>
              </div>
            </div>

            {/* Mint Form */}
            <div style={{
              backgroundColor: 'rgba(55, 65, 81, 0.1)',
              backdropFilter: 'blur(10px)',
              borderRadius: '16px',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              padding: window.innerWidth < 640 ? '16px' : '24px',
              boxShadow: '0 8px 32px rgba(55, 65, 81, 0.1)'
            }}>
              <label style={{
                display: 'block',
                fontWeight: '500',
                color: 'white',
                marginBottom: '8px',
                fontSize: '14px',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
              }}>
                Amount to Mint
              </label>
              <input
                type="number"
                value={mintAmount}
                onChange={(e) => setMintAmount(e.target.value)}
                placeholder="0"
                min="1"
                style={{
                  width: '100%',
                  fontSize: window.innerWidth < 640 ? '18px' : '16px', // Larger font on mobile
                  padding: window.innerWidth < 640 ? '18px 20px' : '16px', // Larger padding on mobile
                  height: window.innerWidth < 640 ? '56px' : '48px', // Taller on mobile for easier touch
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: 'white',
                  borderRadius: '12px',
                  transition: 'all 0.2s ease',
                  outline: 'none',
                  boxSizing: 'border-box',
                  WebkitAppearance: 'none', // Better mobile appearance
                  MozAppearance: 'textfield'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#8b5cf6';
                  e.target.style.boxShadow = '0 0 0 3px rgba(139, 92, 246, 0.2)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <p style={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '14px',
                margin: '12px 0 0',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
              }}>
                Total cost: <span style={{
                  fontWeight: '600',
                  color: '#c084fc',
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                }}>{totalPrice} ETCPOWv2</span>
              </p>
            </div>
          </>
        )}

        {/* Expiration Warning */}
        <div style={{
          backgroundColor: 'rgba(245, 158, 11, 0.1)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          border: '1px solid rgba(245, 158, 11, 0.3)',
          padding: window.innerWidth < 640 ? '16px' : '24px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '16px',
          boxShadow: '0 8px 32px rgba(245, 158, 11, 0.1)'
        }}>
          <div style={{
            marginTop: '2px',
            height: '40px',
            width: '40px',
            backgroundColor: '#f59e0b',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <span style={{
              fontSize: '16px',
              fontWeight: 'bold',
              color: 'white',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)'
            }}>⏰</span>
          </div>
          <div>
            <p style={{
              color: 'white',
              fontWeight: '600',
              fontSize: '16px',
              margin: '0 0 12px 0',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
            }}>License Expiration</p>
            <p style={{
              color: 'rgba(255, 255, 255, 0.8)',
              fontSize: '14px',
              margin: '0',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
              lineHeight: '1.5'
            }}>
              All newly minted licenses have a <span style={{ color: '#fbbf24', fontWeight: '600' }}>6-month validity period</span> from the date of minting.
              After expiration, licenses cannot be used or listed for sale.
            </p>
          </div>
        </div>

        {/* Info Box */}
        <div style={{
          backgroundColor: 'rgba(139, 92, 246, 0.1)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          border: '1px solid rgba(139, 92, 246, 0.3)',
          padding: window.innerWidth < 640 ? '16px' : '24px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '16px',
          boxShadow: '0 8px 32px rgba(139, 92, 246, 0.05)'
        }}>
          <div style={{
            marginTop: '2px',
            height: '40px',
            width: '40px',
            backgroundColor: '#8b5cf6',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <span style={{
              fontSize: '16px',
              fontWeight: 'bold',
              color: 'white',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)'
            }}>!</span>
          </div>
          <div>
            <p style={{
              color: 'white',
              fontWeight: '600',
              fontSize: '16px',
              margin: '0 0 12px 0',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
            }}>Important</p>
            <ul style={{
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '14px',
              margin: '0',
              paddingLeft: '0',
              listStyle: 'none'
            }}>
              <li style={{
                marginBottom: '8px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px'
              }}>
                <span style={{
                  color: '#c084fc',
                  fontSize: '16px'
                }}>•</span>
                <span>ETCPOWv2 tokens required</span>
              </li>
              <li style={{
                marginBottom: '8px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px'
              }}>
                <span style={{
                  color: '#c084fc',
                  fontSize: '16px'
                }}>•</span>
                <span>Each {tiers.find(t => t.id === selectedTier)?.name.toLowerCase() || 'license'} costs {tierPrice} ETCPOWv2</span>
              </li>
              <li style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px'
              }}>
                <span style={{
                  color: '#c084fc',
                  fontSize: '16px'
                }}>•</span>
                <span>Minted licenses are yours immediately</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </BaseModal>
  );
};

export default MintLicensesModal;
